import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link, useParams } from "react-router-dom";
import {
  fetchUsersTestStatus,
  fetchUsersTestAccessStatus,
} from "../../services/api";
import TestPriceModal from "../../components/Modals/TestPricingModal";
import LoginModal     from "../../components/Auth/LoginModel";
import "./TestSeries.css";
import { BlogDetails } from "../../types/blogDetails";

type Status = "started" | "submitted" | undefined;

interface NestedTab {
  key: string;
  label: string;
  children?: string[];
}

interface Test {
  _id: string;
  test_name: string;
  no_of_questions: number;
  test_pay_type: string;
  blog_title_post_slug: string;
  timing: string;
  subject?: string;
  class?: string;
  sort_order?: number;
  user_count?: number;
  live_count?: number;   // how many giving RIGHT NOW
}

interface Props {
  blogDetails: BlogDetails;
  tests: Test[];
}

/* ═══════════════════════════════════════
   SVG ICONS
═══════════════════════════════════════ */
const IcoBook = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);
const IcoTime = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IcoUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const IcoStar = () => (
  <svg viewBox="0 0 24 24" fill="#fbbf24" stroke="none">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const IcoLock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);
const IcoPlay = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="5 3 19 12 5 21 5 3"/></svg>
);
const IcoResume = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);
const IcoResult = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);
const IcoCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IcoProgress = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const IcoInstantResults = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="13 2 3 14 11 14 9 22 21 8 13 8 13 2" />
  </svg>
);

const IcoDetailedSolutions = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <polyline points="8 13 10 15 16 9" />
  </svg>
);

/* ═══════════════════════════════════════
   WHAT'S INCLUDED data
═══════════════════════════════════════ */
// const INCLUDED_ITEMS = [
//   { icon: <IcoBook />, val: "60",  label: "Full Length Tests",    color: "#0d9e8a" },
//   { icon: <IcoBook />, val: "180", label: "Subject-wise Tests",   color: "#7c3aed" },
//   {
//     icon: (
//       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//         <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
//         <polyline points="14 2 14 8 20 8"/>
//       </svg>
//     ),
//     val: "25", label: "Previous Year Papers", color: "#ea580c",
//   },
//   {
//     icon: (
//       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//         <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
//       </svg>
//     ),
//     val: "15", label: "Grand Tests", color: "#f59e0b",
//   },
// ];

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════ */
const TestSeriesTabs: React.FC<Props> = ({ blogDetails, tests }) => {
  const navigate     = useNavigate();
  const { testwise } = useParams();

  const [statuses,         setStatuses]         = useState<Record<string, Status>>({});
  const [isSeriesUnlocked, setIsSeriesUnlocked] = useState(false);
  const [showLoginModal,   setShowLoginModal]   = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedTest,     setSelectedTest]     = useState<Test | null>(null);
  const [loading,          setLoading]          = useState(true);

  /* Simulated live count — replace with real WebSocket/API if available */
  const [liveTotal, setLiveTotal] = useState<number>(0);

  /* ── Tabs state ── */
  const hasTabs  = blogDetails.is_tab === 1;
  const isFlat   = blogDetails.tab_type === "FLAT";
  const isNested = blogDetails.tab_type === "NESTED";
  const tabs     = useMemo(() => blogDetails.tabs ?? [], [blogDetails.tabs]);

  const [activeMainTab, setActiveMainTab] = useState<any>(null);
  const [activeSubTab,  setActiveSubTab]  = useState<string>("");

  useEffect(() => {
    if (!hasTabs || !tabs.length) return;
    setActiveMainTab(tabs[0]);
    if (isNested && (tabs[0] as NestedTab)?.children?.length) {
      setActiveSubTab((tabs[0] as NestedTab).children![0]);
    }
  }, [tabs, hasTabs, isNested]);

  /* ── Fetch user status + access ── */
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const user  = localStorage.getItem("user");
        if (token && user) {
          const userId = JSON.parse(user).id;
          const [statusRes, accessRes] = await Promise.all([
            fetchUsersTestStatus(userId, blogDetails.slug),
            fetchUsersTestAccessStatus(userId, blogDetails.slug),
          ]);
          if (!mounted) return;
          setStatuses(statusRes.data.status);
          setIsSeriesUnlocked(accessRes.data.unlocked);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [blogDetails.slug]);

  /* ── Simulated live count (±1 every 7s) ── */
  useEffect(() => {
    const base = Math.floor((blogDetails.students_count ?? 0) * 0.003) + 2;
    setLiveTotal(base);
    const id = setInterval(() => {
      setLiveTotal(prev => Math.max(1, prev + (Math.random() > 0.5 ? 1 : -1)));
    }, 7000);
    return () => clearInterval(id);
  }, [blogDetails.students_count]);

  /* ── Helpers ── */
  const requireLogin = () => {
    localStorage.setItem("postLoginModelRedirect", window.location.pathname);
    setShowLoginModal(true);
  };

  const handleStart = (test: Test) => {
    if (!localStorage.getItem("token")) return requireLogin();
    const stat = statuses[test._id];
    if (stat === "started")        navigate(`/online-tests/test-page/${test.blog_title_post_slug}/${test._id}`);
    else if (stat === "submitted") navigate(`/online-tests/test-result/${test.blog_title_post_slug}/${test._id}`);
    else                           navigate(`/online-tests/instructions/${test.blog_title_post_slug}/${test._id}`);
  };

  const handleUnlock = (test: Test) => {
    if (!localStorage.getItem("token")) return requireLogin();
    setSelectedTest(test);
    setShowPricingModal(true);
  };

  const handleSeriesEnroll = () => {
    if (!localStorage.getItem("token")) return requireLogin();
    // Enrolling covers the whole series, so there's no single Test document
    // to point to. Passing `null` here meant `testId` reached the backend as
    // `undefined` in Checkout.tsx — if create-order requires a non-empty
    // testId, this is why Enroll (whole series) can fail while Unlock (which
    // always has a real test._id) succeeds. We build a stand-in "test" object
    // instead, so Checkout always receives an _id, clearly tagged as the
    // whole-package purchase rather than a real test row (its _id matches
    // blog._id, which Checkout.tsx uses to detect purchaseType).
    const wholePackagePseudoTest: Test = {
      _id: blogDetails._id,
      test_name: blogDetails.header_title,
      no_of_questions: tests.length,
      test_pay_type: "1",
      blog_title_post_slug: blogDetails.blog_title_post_slug,
      timing: "",
    };
    setSelectedTest(wholePackagePseudoTest);
    setShowPricingModal(true);
  };

  /* ── Filter + sort ── */
  const filteredTests = useMemo(() => {
    let result: Test[] = [];
    if (!hasTabs) {
      result = [...tests];
    } else if (isFlat) {
      result = tests.filter(t => t.subject === (activeMainTab?.label || activeMainTab));
    } else if (isNested) {
      const tab = activeMainTab as NestedTab;
      if (tab?.children?.length) {
        const classNo = tab.label.split(" ")[1];
        result = tests.filter(t => (t.class === classNo || t.class === tab.key) && t.subject === activeSubTab);
      } else {
        result = tests.filter(t => t.subject === tab?.label);
      }
    }
    return [...result].sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
  }, [tests, hasTabs, isFlat, isNested, activeMainTab, activeSubTab]);

  /* ── Progress calculations (across ALL tests, not just filtered tabs) ── */
  const allTestsSorted    = useMemo(() =>
    [...tests].sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999)),
    [tests]
  );
  const completedCount    = allTestsSorted.filter(t => statuses[t._id] === "submitted").length;
  const inProgressCount   = allTestsSorted.filter(t => statuses[t._id] === "started").length;
  const notStartedCount   = allTestsSorted.length - completedCount - inProgressCount;
  const completedPct      = allTestsSorted.length > 0
    ? Math.round((completedCount / allTestsSorted.length) * 100)
    : 0;

  const discPct = blogDetails.original_price && blogDetails.discounted_price
    ? Math.round((1 - Number(blogDetails.discounted_price) / Number(blogDetails.original_price)) * 100)
    : 0;
  const isFree = blogDetails.discounted_price === "0" || blogDetails.discounted_price === "";

  /* ════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════ */
  return (
    <>
      <Helmet>
        {blogDetails?.header_title && (
    <title>{blogDetails.header_title} - Acadebility</title>
  )}

  {blogDetails?.meta_description && (
    <meta
      name="description"
      content={blogDetails.meta_description}
    />
  )}

  {blogDetails?.meta_author && (
    <meta
      name="author"
      content={blogDetails.meta_author}
    />
  )}

  {blogDetails?.meta_keywords && (
    <meta
      name="keywords"
      content={blogDetails.meta_keywords}
    />
  )}

  {/* Open Graph Tags */}
  {blogDetails?.header_title && (
    <meta
      property="og:title"
      content={`${blogDetails.header_title} - Acadebility`}
    />
  )}

  {blogDetails?.meta_description && (
    <meta
      property="og:description"
      content={blogDetails.meta_description}
    />
  )}

  {blogDetails?.image && (
    <meta
      property="og:image"
      content={blogDetails.image}
    />
  )}

  <meta property="og:type" content="website" />

  {/* Use this if your page has the full URL */}
  {/* {blogDetails?.url && (
    <meta
      property="og:url"
      content={blogDetails.url}
    />
  )} */}

  {/* Twitter Cards */}
  <meta
    name="twitter:card"
    content="summary_large_image"
  />

  {blogDetails?.header_title && (
    <meta
      name="twitter:title"
      content={`${blogDetails.header_title} - Acadebility`}
    />
  )}

  {blogDetails?.meta_description && (
    <meta
      name="twitter:description"
      content={blogDetails.meta_description}
    />
  )}

  {blogDetails?.image && (
    <meta
      name="twitter:image"
      content={blogDetails.image}
    />
  )}
      </Helmet>

      <div className="ts-page">

        {/* ── Back Button ── */}
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <Link
            to={`/online-test-series/${testwise}/${blogDetails.test_category_name}`}
            className="ts-back-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Back
          </Link>
        </div>

        {/* ════════════════════════════════
            HERO BANNER
        ════════════════════════════════ */}
        <div className="ts-hero">

          {/* ── Left: everything left-aligned, top-pinned ── */}
          <div className="ts-hero-left">
            {/* Badges */}
            <div className="ts-hero-badges">
              <span className="ts-hero-badge ts-hero-badge-orange">
                <IcoStar /> Flagship Product
              </span>
              <span className="ts-hero-badge ts-hero-badge-glass">
                <IcoStar /> 4.9 Rating
              </span>
            </div>

            {/* Title — left aligned */}
            <h1 className="ts-hero-title">{blogDetails.header_title}</h1>

            {/* Description — left aligned */}
            {blogDetails.description && (
              <p className="ts-hero-desc">{blogDetails.description}</p>
            )}

            {/* Meta row */}
            <div className="ts-hero-meta">
              <span className="ts-hero-meta-item">
                <IcoBook /> {tests.length} Total Tests
              </span>
              <span className="ts-hero-meta-dot">·</span>
              <span className="ts-hero-meta-item">
                <IcoUsers /> {(blogDetails.students_count ?? 0).toLocaleString("en-IN")} Students
              </span>
              <span className="ts-hero-meta-dot">·</span>
              <span className="ts-hero-meta-item">
                <IcoInstantResults /> Instant Results
              </span>
              <span className="ts-hero-meta-dot">·</span>
              <span className="ts-hero-meta-item">
                <IcoDetailedSolutions /> Detailed Solutions
              </span>
            </div>

            {/* Live students pill — blinking red dot */}
            <div className="ts-hero-live">
              <span className="ts-live-dot" />
              <strong>{liveTotal}</strong> students giving tests right now
            </div>
          </div>

          {/* ── Right: Price card — top-pinned ── */}
          <div className="ts-price-card">
            <div className="ts-price-label">Complete Package</div>
            <div className="ts-price-row">
              <span className="ts-price-now">₹{blogDetails.discounted_price}</span>
              {blogDetails.original_price && (
                <span className="ts-price-orig">₹{blogDetails.original_price}</span>
              )}
            </div>
            {discPct > 0 && <div className="ts-price-save">🎉 Save {discPct}%</div>}

            {/* Smart Enroll Button */}
            {isSeriesUnlocked ? (
              <button className="ts-enroll-btn ts-enroll-btn-paid" disabled>
                <IcoCheck /> Already Enrolled
              </button>
            ) : isFree ? (
              <button className="ts-enroll-btn ts-enroll-btn-default" onClick={handleSeriesEnroll}>
                <IcoPlay /> Start Free
              </button>
            ) : (
              <button className="ts-enroll-btn ts-enroll-btn-unlock" onClick={handleSeriesEnroll}>
                <IcoLock /> Enroll Now
              </button>
            )}

            <p className="ts-price-note">Access all {tests.length} tests instantly</p>
            <div className="ts-price-note-green">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              No hidden charges
            </div>
          </div>
        </div>

        {/* ════════════════════════════════
            WHAT'S INCLUDED
        ════════════════════════════════ */}
        {/* <div className="ts-included">
          <div className="ts-included-head">
            <div className="ts-included-head-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#0d9e8a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
            </div>
            <div>
              <div className="ts-included-title">What's Included</div>
              <div className="ts-included-sub">Everything you get with this package</div>
            </div>
          </div>
          <div className="ts-included-grid">
            {INCLUDED_ITEMS.map(it => (
              <div className="ts-included-item" key={it.label}>
                <div className="ts-included-item-icon" style={{ background: `${it.color}1a`, color: it.color }}>
                  {it.icon}
                </div>
                <div className="ts-included-item-val" style={{ color: it.color }}>{it.val}</div>
                <div className="ts-included-item-label">{it.label}</div>
              </div>
            ))}
          </div>
        </div> */}

        {/* ════════════════════════════════
            TEST PROGRESS  (across ALL tests)
        ════════════════════════════════ */}
        {!loading && allTestsSorted.length > 0 && localStorage.getItem("token") && (
          <div className="ts-progress-block">
            {/* Head */}
            <div className="ts-progress-head">
              <div className="ts-progress-title">
                <IcoProgress />
                Your Progress
              </div>
              <div className="ts-progress-pct-label">{completedPct}%</div>
            </div>

            {/* 4 stat pills */}
            <div className="ts-progress-stats">
              {/* Completed */}
              <div className="ts-progress-stat">
                <div className="ts-progress-stat-icon" style={{ background: "#f0fdf4", color: "#16a34a" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div>
                  <div className="ts-progress-stat-val" style={{ color: "#16a34a" }}>{completedCount}</div>
                  <div className="ts-progress-stat-label">Completed</div>
                </div>
              </div>
              {/* In Progress */}
              <div className="ts-progress-stat">
                <div className="ts-progress-stat-icon" style={{ background: "#eff6ff", color: "#3b82f6" }}>
                  <IcoTime />
                </div>
                <div>
                  <div className="ts-progress-stat-val" style={{ color: "#3b82f6" }}>{inProgressCount}</div>
                  <div className="ts-progress-stat-label">In Progress</div>
                </div>
              </div>
              {/* Not Started */}
              <div className="ts-progress-stat">
                <div className="ts-progress-stat-icon" style={{ background: "#fff7ed", color: "#f97316" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <div>
                  <div className="ts-progress-stat-val" style={{ color: "#f97316" }}>{notStartedCount}</div>
                  <div className="ts-progress-stat-label">Not Started</div>
                </div>
              </div>
              {/* Total */}
              <div className="ts-progress-stat">
                <div className="ts-progress-stat-icon" style={{ background: "var(--ts-teal-pale)", color: "var(--ts-teal)" }}>
                  <IcoBook />
                </div>
                <div>
                  <div className="ts-progress-stat-val">{allTestsSorted.length}</div>
                  <div className="ts-progress-stat-label">Total Tests</div>
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="ts-progress-bar-wrap">
              <div className="ts-progress-track">
                <div className="ts-progress-fill" style={{ width: `${completedPct}%` }} />
              </div>
              <div className="ts-progress-markers">
                <span>0%</span>
                <span style={{ color: completedPct >= 50 ? "var(--ts-teal)" : "var(--ts-text-muted)" }}>50%</span>
                <span style={{ color: completedPct === 100 ? "#16a34a" : "var(--ts-text-muted)" }}>100%</span>
              </div>
            </div>

            {/* Motivational message */}
            <div style={{
              marginTop: 12, fontSize: "0.82rem", fontWeight: 700,
              color: completedPct === 100 ? "#16a34a" : "var(--ts-text-muted)",
            }}>
              {completedPct === 0
                ? "🚀 Start your first test to begin tracking progress!"
                : completedPct === 100
                  ? "🎉 Amazing! You've completed all tests in this series."
                  : completedPct >= 75
                    ? `🔥 Almost there! ${notStartedCount} test${notStartedCount !== 1 ? "s" : ""} left to complete.`
                    : `📈 Keep going! ${completedCount} done, ${notStartedCount} to go.`
              }
            </div>
          </div>
        )}

        {/* ════════════════════════════════
            TABS (main + sub)
        ════════════════════════════════ */}
        {hasTabs && (
          <div className="ts-tabs-wrap">
            <div className="ts-main-tabs">
              {tabs.map((tab: any) => (
                <button
                  key={tab.key || tab}
                  className={`ts-main-tab${activeMainTab === tab ? " active" : ""}`}
                  onClick={() => {
                    setActiveMainTab(tab);
                    setActiveSubTab(tab.children?.[0] || "");
                  }}
                >
                  {tab.label || tab}
                </button>
              ))}
            </div>

            {/* Sub tabs */}
            {isNested && (activeMainTab as NestedTab)?.children && (
              <div className="ts-sub-tabs">
                {(activeMainTab as NestedTab).children!.map(sub => (
                  <button
                    key={sub}
                    className={`ts-sub-tab${activeSubTab === sub ? " active" : ""}`}
                    onClick={() => setActiveSubTab(sub)}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════
            LIST HEADER  (title + live pill)
        ════════════════════════════════ */}
        <div className="ts-list-header">
          <div className="ts-list-title">
            {hasTabs && activeMainTab
              ? `${activeMainTab.label || activeMainTab}${activeSubTab ? ` — ${activeSubTab}` : ""}`
              : "All Tests"}
            <span className="ts-list-count">({filteredTests.length})</span>
          </div>
          <div className="ts-list-live">
            <span className="ts-live-dot" />
            {liveTotal} giving now
          </div>
        </div>

        {/* ════════════════════════════════
            TEST CARDS
        ════════════════════════════════ */}
        <div className="ts-tests-list">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div className="ts-skeleton-card" key={i}>
                <div className="ts-sk ts-sk-circle" />
                <div className="ts-sk-lines">
                  <div className="ts-sk ts-sk-title" />
                  <div className="ts-sk ts-sk-meta" />
                </div>
                <div className="ts-sk ts-sk-btn" />
              </div>
            ))
          ) : filteredTests.length === 0 ? (
            <div style={{
              textAlign: "center", padding: "48px 24px",
              background: "var(--ts-white)", borderRadius: "var(--ts-radius)",
              border: "1.5px solid var(--ts-border)",
              color: "var(--ts-text-muted)", fontWeight: 700,
            }}>
              <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>📋</div>
              No tests found for this section
            </div>
          ) : (
            filteredTests.map((test, idx) => {
              const stat      = statuses[test._id];
              const isPremium = test.test_pay_type === "1" && !isSeriesUnlocked;
              /* Simulated per-test live: replace with test.live_count from API */
              const testLive  = test.live_count ??
                (idx === 0 ? Math.max(1, Math.floor(liveTotal * 0.4))
                : idx === 1 ? Math.floor(liveTotal * 0.3)
                : 0);

              const cardClass = `ts-test-card ${
                stat === "submitted" ? "ts-test-card-done"
                : stat === "started"  ? "ts-test-card-active"
                : isPremium           ? "ts-test-card-premium"
                : ""
              }`;

              return (
                <div className={cardClass} key={test._id}>

                  {/* Number bubble */}
                  <div className="ts-test-num">{idx + 1}</div>

                  {/* Info — LEFT ALIGNED */}
                  <div className="ts-test-info">
                    <div className="ts-test-name">{test.test_name}</div>
                    <div className="ts-test-meta-row">
                      <span className="ts-test-meta-item">
                        <IcoBook />
                        {test.no_of_questions} Questions
                      </span>
                      <span className="ts-test-meta-item">
                        <IcoTime />
                        {test.timing} Min
                      </span>
                      {(test.user_count ?? 0) > 0 && (
                        <span className="ts-test-meta-item">
                          <IcoUsers />
                          {(test.user_count!).toLocaleString("en-IN")} Attempted
                        </span>
                      )}
                      {/* Per-test live count with blinking red dot */}
                      {testLive > 0 && (
                        <span className="ts-meta-live">
                          <span className="ts-meta-live-dot" />
                          {testLive} live now
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: badge + button */}
                  <div className="ts-test-right">
                    {stat !== "started" && stat !== "submitted" && (
                      test.test_pay_type === "0"
                        ? <span className="ts-free-badge"><IcoStar /> Free</span>
                        : null
                    )}

                    <button
                      className={`ts-action-btn ${
                        stat === "started"     ? "ts-btn-resume"
                        : stat === "submitted" ? "ts-btn-result"
                        : isPremium            ? "ts-btn-unlock"
                        : "ts-btn-start"
                      }`}
                      onClick={() => isPremium ? handleUnlock(test) : handleStart(test)}
                    >
                      {stat === "started"
                        ? <><IcoResume /> Resume</>
                        : stat === "submitted"
                          ? <><IcoResult /> View Result</>
                          : isPremium
                            ? <><IcoLock /> Unlock</>
                            : <><IcoPlay /> Start Now</>
                      }
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {showLoginModal && (
        <LoginModal
          show
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={() => {}}
        />
      )}
      {showPricingModal && (
        <TestPriceModal
          show
          onClose={() => setShowPricingModal(false)}
          discounted_price={blogDetails.discounted_price}
          original_price={blogDetails.original_price}
          blogTitle={blogDetails.header_title}
          test_category_name={blogDetails.test_category_name}
          onCheckout={() => {
            setShowPricingModal(false);
            navigate("/checkout", { state: { blog: blogDetails, test: selectedTest } });
          }}
        />
      )}
    </>
  );
};

export default TestSeriesTabs;